import {
  PageContentContainer,
  PageLoading,
} from "@fp/ui/components/base/page-layout/page-layout";
import { AdminLayout } from "@/components/admin-layout";
import { AdminPageHeader } from "@/components/admin-page-header";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import { toast } from "@/lib/toast";
import { AxiosError } from "axios";
import axios from "@/lib/axios";
import { getGetApiOnboardingProgressQueryKey } from "@fp/admin-api-client/onboarding-progress/onboarding-progress.client";

interface ApiErrorResponse {
  message?: string;
}

export const Route = createFileRoute("/callback/astra")({
  component: RouteComponent,
  validateSearch: (search: Record<string, unknown>) => {
    return {
      code: search.code as string | undefined,
      error: search.error as string | undefined,
      user_intent_id: search.user_intent_id as string | undefined,
    };
  },
});

function RouteComponent() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { code, error, user_intent_id } = Route.useSearch();
  const hasProcessed = useRef(false);

  const completeVerificationMutation = useMutation({
    mutationFn: async (params: { authorizationCode?: string; userIntentId?: string }) => {
      const response = await axios.post("/api/admin/funding/complete-identity-verification", {
        authorizationCode: params.authorizationCode,
        userIntentId: params.userIntentId,
      });
      return response.data;
    },
    onSuccess: (data: { status: string }) => {
      queryClient.invalidateQueries({ queryKey: getGetApiOnboardingProgressQueryKey() });
      const message = data.status === "Approved"
        ? "Credentials linked successfully"
        : "Business verification submitted";
      toast.success(message);
      navigate({ to: "/funding" });
    },
    onError: (err: AxiosError<ApiErrorResponse>) => {
      toast.error(err.response?.data?.message || "Failed to complete verification");
      navigate({ to: "/funding" });
    },
  });

  useEffect(() => {
    // Prevent duplicate processing
    if (hasProcessed.current) {
      return;
    }
    hasProcessed.current = true;

    if (error) {
      toast.error("Verification was cancelled or failed");
      navigate({ to: "/funding" });
      return;
    }

    completeVerificationMutation.mutate({ authorizationCode: code, userIntentId: user_intent_id });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <AdminLayout>
      <AdminPageHeader
        title="Identity Verification"
        parent={{ url: "/funding", label: "Funding" }}
      />
      <PageContentContainer>
        <div className="flex flex-col items-center justify-center min-h-[400px]">
          <PageLoading />
          <p className="mt-4 text-gray-600">Completing verification...</p>
        </div>
      </PageContentContainer>
    </AdminLayout>
  );
}